'use client'

import * as ConfirmDialogPrimitive from '@radix-ui/react-dialog'
import * as React from 'react'

import { cn } from '../../lib'
import { VStack } from '../layout'

/**
 * @deprecated Use ConfirmDialog instead
 */
const ConfirmDialogV1 = ConfirmDialogPrimitive.Root

const ConfirmDialogV1Trigger = ConfirmDialogPrimitive.Trigger

const ConfirmDialogV1Portal = ConfirmDialogPrimitive.Portal

const ConfirmDialogV1Overlay = React.forwardRef<
  React.ElementRef<typeof ConfirmDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof ConfirmDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <ConfirmDialogPrimitive.Overlay
    className={cn(
      'fixed inset-0 z-50 bg-[#1C1C1C]/70 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
    ref={ref}
  />
))

const ConfirmDialogV1Content = React.forwardRef<
  React.ElementRef<typeof ConfirmDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof ConfirmDialogPrimitive.Content> & {
    onClose?: () => void
    isInternalDialog?: boolean
  }
>(({ className, onClose, isInternalDialog = true, ...props }, ref) => {
  return (
    <ConfirmDialogV1Portal>
      {isInternalDialog && <ConfirmDialogV1Overlay onClick={onClose} />}
      <ConfirmDialogPrimitive.Content
        ref={ref}
        style={{
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 51,
          ...props.style,
        }}
        className={cn(
          'fixed bg-white z-[51] rounded-lg border shadow-lg',
          'left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]',
          'grid w-[calc(100dvw-40px)] mobile:min-w-[360px] tablet:max-w-[480px] gap-8 p-5',
          [
            'duration-200',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
            'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
          ],
          className,
        )}
        {...props}
      />
    </ConfirmDialogV1Portal>
  )
})

const ConfirmDialogV1Header = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <VStack className={cn('space-y-[5px] gap-5', className)} {...props} />
)

const ConfirmDialogV1Footer = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <VStack className={cn('flex-col-reverse', className)} {...props} />
)

const ConfirmDialogV1Title = React.forwardRef<
  React.ElementRef<typeof ConfirmDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof ConfirmDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <ConfirmDialogPrimitive.Title
    ref={ref}
    className={cn('flex text-2xl font-semibold min-h-12 items-center justify-center', className)}
    {...props}
  />
))

const ConfirmDialogV1Description = React.forwardRef<
  React.ElementRef<typeof ConfirmDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof ConfirmDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <ConfirmDialogPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
))

const ConfirmDialogV1Close = React.forwardRef<
  React.ElementRef<typeof ConfirmDialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof ConfirmDialogPrimitive.Close>
>(({ className, ...props }, ref) => (
  <ConfirmDialogPrimitive.Close ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
))

ConfirmDialogV1Overlay.displayName = ConfirmDialogPrimitive.Overlay.displayName
ConfirmDialogV1Content.displayName = ConfirmDialogPrimitive.Content.displayName
ConfirmDialogV1Header.displayName = 'ConfirmDialogV1Header'
ConfirmDialogV1Footer.displayName = 'ConfirmDialogV1Footer'
ConfirmDialogV1Title.displayName = ConfirmDialogPrimitive.Title.displayName
ConfirmDialogV1Description.displayName = ConfirmDialogPrimitive.Description.displayName
ConfirmDialogV1Close.displayName = ConfirmDialogPrimitive.Close.displayName

export {
  ConfirmDialogV1,
  ConfirmDialogV1Close,
  ConfirmDialogV1Content,
  ConfirmDialogV1Description,
  ConfirmDialogV1Footer,
  ConfirmDialogV1Header,
  ConfirmDialogV1Overlay,
  ConfirmDialogV1Portal,
  ConfirmDialogV1Title,
  ConfirmDialogV1Trigger,
}
